import { Box, Checkbox, CircularProgress, Divider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ModalHelpClub from '~/modals/ModalHelpClub';
import ModalHelpFinancialCharges from '~/modals/ModalHelpFinancialCharges';
import useStore from '~/store';
import { resolveClubDisplayAmount, shouldShowFinancialCharges } from '~/pages/PersonalCoverage/utils/planType.utils';
import { EquipmentValue } from './EquipmentValue';
import { InsuredInfo } from './InsuredInfo';
import * as styles from './styles';
import { TypesOfEquipment } from './TypesOfEquipment';

interface Props {
  showProduct?: boolean;
  loading?: boolean;
}

interface ClubData {
  club_name: string;
  has_club: boolean;
  flat_rate: number;
  club_description: string;
}

const MessageList = ({ showProduct = false, loading = false }: Props) => {
  const [clubData, setClubData] = useState<ClubData>();

  const {
    quoteResponse,
    totalValue,
    serviceCharge,
    products,
    flat_rate,
    has_club,
    club_description,
    club_name,
    clubType,
    planType,
    membershipFee,
    promotionData,
    promotionalMonths
  } = useStore((store) => ({
    totalValue: store.totalValue,
    serviceCharge: store.serviceCharge,
    products: store.products,
    quoteResponse: store.quoteResponse,
    flat_rate: store.flat_rate,
    has_club: store.has_club,
    club_name: store.club_name,
    club_description: store.club_description,
    clubType: store.quoteResponse?.type_club,
    planType: store.quoteResponse?.plan_type,
    membershipFee: store.quoteResponse?.membership_fee,
    promotionData: store.promotionData,
    promotionalMonths: store.quoteResponse?.promotional_months
  }));

  const isOnlyProduct9 = products?.length === 1 && products[0]?.product_id === '9';
  const clubDisplayAmount = resolveClubDisplayAmount(clubData?.flat_rate ?? 0, totalValue, planType);
  const showFinancialCharges = shouldShowFinancialCharges(planType, isOnlyProduct9);

  useEffect(() => {
    if (quoteResponse) {
      setClubData({
        club_name: quoteResponse.plan_club_name,
        has_club: quoteResponse.plan_has_club,
        flat_rate: quoteResponse.flat_rate,
        club_description: quoteResponse.plan_club_description
      });
    }

    if (has_club) {
      setClubData({
        club_name: club_name,
        has_club: has_club,
        flat_rate: flat_rate,
        club_description: club_description
      });
    }
  }, [quoteResponse, flat_rate, has_club, club_name]);

  const priceFormatter = (value: number) => {
    const formattedValue = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      style: 'currency',
      currency: 'BRL'
    }).format(value);

    return formattedValue;
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        textAlign: 'center',
        display: { xs: 'flex', md: 'initial' },
        flexDirection: { xs: 'column', md: 'initial' },
        justifyContent: { xs: 'center', md: 'initial' },
        alignItems: { xs: 'center', md: 'initial' }
      }}
    >
      <Typography variant="h3" sx={styles.descritionTitle}>
        Detalhes da contratação
      </Typography>
      <Divider sx={styles.divider} />
      <InsuredInfo />
      <Typography variant="h6" fontWeight="bold">
        Equipamento segurado:
      </Typography>
      <Box sx={{ minWidth: '100%' }}>
        <TypesOfEquipment />
      </Box>
      <Box sx={{ minWidth: '100%' }}>
        <EquipmentValue />
      </Box>
      {showProduct && (
        <>
          <Typography variant="h6" fontWeight="bold" sx={styles.divider}>
            Serviço(s) contratado(s):
          </Typography>
          {products?.length > 0 &&
            products.map((product) => {
              return (
                <Box key={product.product_id} sx={() => styles.containerMessage}>
                  <Checkbox
                    checked
                    inputProps={{
                      'aria-label': product.product_name
                    }}
                    color="success"
                  />
                  <Typography sx={styles.productName} variant="h6" fontWeight="bold" color="primary">
                    {product.product_name}
                  </Typography>
                </Box>
              );
            })}
        </>
      )}
      <Divider sx={styles.amountDivider} />

      {promotionData?.has_promotion ? (
        <>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Valor promocional
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
              {promotionalMonths && promotionalMonths > 0
                ? `${promotionalMonths} Meses GRÁTIS`
                : 'Gratuito até 31 de dezembro'}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 2,
              color: '#A547FF',
              borderTop: '1px solid #323232',
              marginInline: 'auto',
              paddingTop: '1rem'
            }}
          >
            {promotionalMonths && promotionalMonths > 0
              ? 'A partir da data de contratação.'
              : 'Válido para todos os usuários.'}
          </Typography>
        </>
      ) : (
        <>
          {clubType === 2 ? (
            <>
              <Box sx={styles.containerAmount}>
                <Box component="section" sx={styles.FinancialCharges}>
                  <Typography variant="body1">Taxa de adesão</Typography>
                </Box>
                <Typography variant="body1" sx={styles.amountStyle}>
                  {loading ? <CircularProgress size={16} /> : '1x ' + priceFormatter(membershipFee)}
                </Typography>
              </Box>
              <Box sx={styles.containerAmount}>
                <Typography variant="body1" sx={styles.totalStyle}>
                  Plano anual {clubData?.club_name}
                </Typography>
                <Typography variant="body1" sx={styles.amountStyle}>
                  12x {loading ? <CircularProgress size={20} /> : priceFormatter(totalValue)}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {!!clubData && clubData.has_club ? (
                <>
                  <Box sx={styles.containerAmount}>
                    <Box component="section" sx={styles.FinancialCharges}>
                      <Typography variant="body1">{clubData.club_name}</Typography>
                      <ModalHelpClub clubDiscription={clubData.club_description} />
                    </Box>
                    <Typography variant="body1">
                      {loading ? <CircularProgress size={16} /> : priceFormatter(clubDisplayAmount)}
                    </Typography>
                  </Box>
                </>
              ) : null}

              {showFinancialCharges && (
                <Box sx={styles.containerAmount}>
                  <Box component="section" sx={styles.FinancialCharges}>
                    <Typography variant="body1">Encargos financeiros</Typography>
                    <ModalHelpFinancialCharges />
                  </Box>
                  <Typography variant="body1">
                    {loading ? <CircularProgress size={16} /> : priceFormatter(serviceCharge)}
                  </Typography>
                </Box>
              )}

              {showFinancialCharges && <Divider sx={styles.clubDivider} />}

              <Box sx={styles.containerAmount}>
                <Typography variant="body1" sx={styles.totalStyle}>
                  Valor total
                </Typography>
                <Typography variant="h6" sx={styles.amountStyle}>
                  12x {loading ? <CircularProgress size={20} /> : priceFormatter(isOnlyProduct9 ? 0 : totalValue)}
                </Typography>
              </Box>
              {membershipFee > 0 && !isOnlyProduct9 && (
                <>
                  <Divider sx={styles.amountDivider} />
                  <Box sx={styles.containerAmount}>
                    <Typography variant="body1" sx={styles.totalStyle}>
                      Taxa de adesão
                    </Typography>
                    <Typography variant="h6" sx={styles.amountStyle}>
                      1x {loading ? <CircularProgress size={20} /> : priceFormatter(membershipFee)}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};
export default MessageList;
